import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

export type ColorMode = 'system' | 'light' | 'dark' | 'contrast';
export type TextScale = '100' | '112' | '125';

interface AccessibilityPreferences {
  colorMode: ColorMode;
  textScale: TextScale;
  reduceMotion: boolean;
}

interface AccessibilityPreferencesContextValue extends AccessibilityPreferences {
  setColorMode: (mode: ColorMode) => void;
  setTextScale: (scale: TextScale) => void;
  setReduceMotion: (reduce: boolean) => void;
  reset: () => void;
}

const STORAGE_KEY = 'orun-accessibility';
const defaults: AccessibilityPreferences = {
  colorMode: 'system',
  textScale: '100',
  reduceMotion: false,
};

const AccessibilityPreferencesContext =
  createContext<AccessibilityPreferencesContextValue | null>(null);

function readPreferences(): AccessibilityPreferences {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return defaults;
    const parsed = JSON.parse(stored) as Partial<AccessibilityPreferences>;
    return {
      colorMode: ['system', 'light', 'dark', 'contrast'].includes(parsed.colorMode ?? '')
        ? (parsed.colorMode as ColorMode)
        : defaults.colorMode,
      textScale: ['100', '112', '125'].includes(parsed.textScale ?? '')
        ? (parsed.textScale as TextScale)
        : defaults.textScale,
      reduceMotion: Boolean(parsed.reduceMotion),
    };
  } catch {
    return defaults;
  }
}

export function AccessibilityPreferencesProvider({ children }: PropsWithChildren) {
  const [preferences, setPreferences] = useState<AccessibilityPreferences>(readPreferences);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.colorMode = preferences.colorMode;
    root.dataset.textScale = preferences.textScale;
    root.dataset.reduceMotion = preferences.reduceMotion ? 'true' : 'false';
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch {
      // Las preferencias siguen funcionando aunque el navegador bloquee el almacenamiento.
    }
  }, [preferences]);

  const value = useMemo<AccessibilityPreferencesContextValue>(
    () => ({
      ...preferences,
      setColorMode: (colorMode) => setPreferences((current) => ({ ...current, colorMode })),
      setTextScale: (textScale) => setPreferences((current) => ({ ...current, textScale })),
      setReduceMotion: (reduceMotion) =>
        setPreferences((current) => ({ ...current, reduceMotion })),
      reset: () => setPreferences(defaults),
    }),
    [preferences],
  );

  return (
    <AccessibilityPreferencesContext.Provider value={value}>
      {children}
    </AccessibilityPreferencesContext.Provider>
  );
}

export function useAccessibilityPreferences() {
  const context = useContext(AccessibilityPreferencesContext);
  if (!context) {
    throw new Error('AccessibilityPreferencesProvider no está disponible.');
  }
  return context;
}
